import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import { receiveCartProducts } from 'pages/MyAccount/services/actions/productsActions';

import {
  takeEvery,
  takeLatest,
  call,
  put,
  select,
  throttle,
} from 'redux-saga/effects';

import {
  getTeamId,
  getInvitationsAndTeam,
  createTeamRequest,
  getInvitesRemaining,
  getTeamCarts,
  getTeamAvatars,
} from 'pages/MyAccount/services/api/social-ecomm';

import {
  SOCIAL_ECOMM,
  requestTeamId,
  receiveTeamId,
  receiveTeamIdError,
  receiveInvitationsAndTeam,
  receiveInvitationsAndTeamError,
  createTeamSuccess,
  createTeamError,
  requestInvitesRemaining,
  receiveInvitesRemaining,
  receiveInvitesRemainingError,
  requestTeamCarts,
  receiveTeamCarts,
  receiveTeamCartsError,
  receiveTeamAvatars,
  receiveTeamAvatarsError,
} from 'pages/MyAccount/services/actions/socialEcommActions';

const {
  REQUEST_INVITES_REMAINING,
  REQUEST_INVITES_REMAINING_DEBOUNCE,
  REQUEST_INVITATIONS_AND_TEAM,
  REQUEST_TEAM_CARTS,
  RECEIVE_TEAM_CARTS,
  CREATE_TEAM,
  REQUEST_WIDGET_DATA,
} = SOCIAL_ECOMM;

function* fetchSocialTeamId(campaignId, campaignWindow) {
  try {
    yield put(requestTeamId());
    const response = yield call(getTeamId, campaignId, campaignWindow);
    const {
      data: { items },
    } = response;
    yield put(receiveTeamId(items[0]));
  } catch (err) {
    yield put(receiveTeamIdError(err));
  }
}

function* fetchInvitationsAndTeam() {
  const campaignState = yield select((state) => state.campaignState);
  const { campaignId, socialWindow } = campaignState;
  try {
    const response = yield call(
      getInvitationsAndTeam,
      campaignId,
      socialWindow
    );
    const {
      data: { items },
    } = response;
    yield put(receiveInvitationsAndTeam(items[0]));
  } catch (err) {
    yield put(receiveInvitationsAndTeamError(err));
  }
}

function* fetchTeamAvatars(userIds, socialEcommFF, disableAvatarsFF) {
  if (socialEcommFF && !disableAvatarsFF) {
    try {
      const socialAvatars = yield call(getTeamAvatars, userIds);
      const socialAvatarsIds = map(socialAvatars, (item) => item.userId);
      yield put(receiveTeamAvatars(socialAvatars, socialAvatarsIds));
    } catch (err) {
      yield put(receiveTeamAvatarsError(err));
    }
  }
}

function* fetchWidgetAvatars(socialEcommFF, disableAvatarsFF) {
  if (socialEcommFF && !disableAvatarsFF) {
    const redeemedMembers = yield select(
      (state) => state.socialEcommState.redeemedMembers
    );
    const redeemedMemberIds = map(
      redeemedMembers,
      (redeemedMember) => redeemedMember.userId
    );
    if (!isEmpty(redeemedMemberIds))
      yield call(
        fetchTeamAvatars,
        redeemedMemberIds,
        socialEcommFF,
        disableAvatarsFF
      );
  }
}

function* fetchWidgetData(action) {
  const { socialEcommFF, disableAvatarsFF } = action;
  yield call(fetchInvitationsAndTeam);
  yield call(fetchWidgetAvatars, socialEcommFF, disableAvatarsFF);
}

function* fetchTeamCartsAvatars(action) {
  const { socialEcommFF, disableAvatarsFF } = action;
  const userIds = yield select(
    (state) => state.socialEcommState.teamCart.userIds
  );
  yield call(fetchTeamAvatars, userIds, socialEcommFF, disableAvatarsFF);
}

function* fetchInvitesRemaining() {
  const teamId = yield select((state) => state.socialEcommState.team.teamId);
  try {
    const response = yield call(getInvitesRemaining, teamId);
    yield put(receiveInvitesRemaining(response));
  } catch (err) {
    yield put(receiveInvitesRemainingError(err));
  }
}

function* fetchInvitesRemainingDebounce() {
  const teamId = yield select((state) => state.socialEcommState.team.teamId);
  if (teamId) yield put(requestInvitesRemaining());
}

function* createTeam() {
  const campaignState = yield select((state) => state.campaignState);
  const { campaignId, socialWindow } = campaignState;
  try {
    const response = yield call(createTeamRequest, campaignId, socialWindow);
    const {
      data: { items },
    } = response;
    const data = items[0];
    yield put(createTeamSuccess(data));
    yield put(requestInvitesRemaining());
  } catch (err) {
    yield put(createTeamError(err));
  }
}

function* fetchTeamCarts(action) {
  const { socialEcommFF, disableAvatarsFF } = action;
  const teamId = yield select((state) => state.socialEcommState.team.teamId);
  try {
    const response = yield call(getTeamCarts, teamId);
    const {
      cartProducts,
      userIds,
      cartVariants,
      products,
      variants,
    } = response;
    yield put(
      receiveTeamCarts(
        cartProducts,
        userIds,
        cartVariants,
        socialEcommFF,
        disableAvatarsFF
      )
    );
    yield put(receiveCartProducts({ products, variants }));
  } catch (err) {
    yield put(receiveTeamCartsError(err));
  }
}

export function* fetchSocialEcommData(flags) {
  const {
    socialEcomm: socialEcommFF,
    disableTeamCartsAndAvatars: disableTeamCartsAndAvatarsFF,
    disableAvatars: disableAvatarsFF,
  } = flags;
  const campaignState = yield select((state) => state.campaignState);
  const { campaignId, socialWindow } = campaignState;
  if (socialEcommFF) yield call(fetchSocialTeamId, campaignId, socialWindow);
  const teamId = yield select((state) => state.socialEcommState.team.teamId);
  if (teamId && socialEcommFF && !disableTeamCartsAndAvatarsFF) {
    yield put(requestTeamCarts(socialEcommFF, disableAvatarsFF));
  }
}

// watcher sagas
export function* watchSocialEcommData() {
  yield throttle(
    60000,
    REQUEST_INVITES_REMAINING_DEBOUNCE,
    fetchInvitesRemainingDebounce
  );
  yield takeEvery(REQUEST_INVITES_REMAINING, fetchInvitesRemaining);
  yield takeEvery(REQUEST_TEAM_CARTS, fetchTeamCarts);
  yield takeEvery(RECEIVE_TEAM_CARTS, fetchTeamCartsAvatars);
  yield takeEvery(REQUEST_INVITATIONS_AND_TEAM, fetchInvitationsAndTeam);
  yield takeEvery(REQUEST_WIDGET_DATA, fetchWidgetData);
  yield takeLatest(CREATE_TEAM, createTeam);
}
